import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import AddressModal from './AddressModal';
import { Address, MapboxFeature } from '../types';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL || '';


mapboxgl.accessToken = 'pk.eyJ1IjoiY292ZXJ6b25lIiwiYSI6ImNtMG03eDYxODA2YTYya3NheW4wM25nbjcifQ.zZ6gyVv7yu7kF-gmWRL-Qw';

interface AddressTabProps {
  clientId?: string;
  onAddressChange?: () => void; // ✅ ADD THIS
}

const AddressTab = ({ clientId, onAddressChange }: AddressTabProps) => {
  const { can } = usePermissions();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [newAddress, setNewAddress] = useState<Address>({ street: '', city: '', state: '', province: '', zipCode: '' });
  const [addressErrors, setAddressErrors] = useState({ street: '', city: '', state: '', province: '', zipCode: '', server: '' });
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<MapboxFeature[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch addresses
  useEffect(() => {
    if (!clientId || isNaN(parseInt(clientId))) {
      setAddressErrors((prev) => ({ ...prev, server: 'Invalid Client ID' }));
      return;
    }
    const fetchAddresses = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_URL}/api/addresses?clientId=${clientId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Sort addresses by ID to maintain consistent order
        const sortedAddresses = response.data.sort((a: Address, b: Address) => {
        // Handle cases where id might be undefined
        if (a.id === undefined || b.id === undefined) return 0;
        return a.id - b.id;
      });

        setAddresses(sortedAddresses);
        setAddressErrors((prev) => ({ ...prev, server: '' }));
      } catch (error: any) {
        console.error('Error fetching addresses:', error);
        setAddressErrors((prev) => ({
          ...prev,
          server: error.response?.data?.message || 'Failed to load addresses. Please check the server.',
        }));
      }
    };
    fetchAddresses();
  }, [clientId]);

  // Fetch address suggestions from Mapbox
  const fetchAddressSuggestions = useCallback(async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${
          mapboxgl.accessToken
        }&types=address&country=ZA&limit=10`
      );
      const data = await response.json();
      console.log('Suggestions fetched:', data.features);
      setAddressSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  // Handle address selection from Mapbox suggestions
const handleAddressSelect = (feature: MapboxFeature) => {
  console.log('📍 Selected address feature:', feature);
  
  let street = feature.place_name.split(',')[0].trim();
  let city = '';
  let state = '';    // Suburb
  let province = ''; // Province
  let zipCode = '';

  // Debug: Log all context items with their types
  console.log('🔍 Mapbox context analysis:');
  feature.context?.forEach((ctx, index) => {
    console.log(`  [${index}] ${ctx.id} = "${ctx.text}"`);
  });

  // Extract address components from Mapbox context more specifically
  feature.context?.forEach((ctx) => {
    const ctxId = ctx.id;
    
    if (ctxId.startsWith('place.')) {
      city = ctx.text;
      console.log('🏙️ City found:', city);
    }
    else if (ctxId.startsWith('locality.') || ctxId.startsWith('neighborhood.')) {
      // Suburb is often in locality or neighborhood for South African addresses
      if (!state) state = ctx.text; // Only set if we don't have suburb yet
      console.log('🏘️ Suburb found:', ctx.text);
    }
    else if (ctxId.startsWith('region.')) {
      // Region is the province - this should be the only one
      province = ctx.text;
      console.log('🏞️ Province found:', province);
    }
    else if (ctxId.startsWith('postcode.')) {
      zipCode = ctx.text;
      console.log('📮 Postal code found:', zipCode);
    }
    else {
      console.log('⚡ Other context item:', ctxId, '=', ctx.text);
    }
  });

  // If we didn't find suburb, try to extract it from place_name
  if (!state) {
    const placeParts = feature.place_name.split(',');
    if (placeParts.length >= 2) {
      const potentialSuburb = placeParts[1]?.trim();
      if (potentialSuburb && potentialSuburb !== city && !potentialSuburb.match(/^\d+$/)) {
        state = potentialSuburb;
        console.log('🏘️ Suburb extracted from place_name:', state);
      }
    }
  }

  // Clean up province - remove any numbers (postal codes)
  if (province) {
    province = province.replace(/\d+/g, '').trim();
    console.log('🧹 Cleaned province:', province);
  }

  // If province is a short code, map it to full name
  if (province && province.length <= 3) {
    const provinceMap: { [key: string]: string } = {
      'KZN': 'KwaZulu-Natal',
      'NL': 'KwaZulu-Natal',
      'EC': 'Eastern Cape', 
      'WC': 'Western Cape',
      'NC': 'Northern Cape',
      'FS': 'Free State',
      'GT': 'Gauteng',
      'LP': 'Limpopo',
      'MP': 'Mpumalanga',
      'NW': 'North West'
    };
    const mappedProvince = provinceMap[province.toUpperCase()];
    if (mappedProvince) {
      province = mappedProvince;
      console.log('🗺️ Mapped province:', mappedProvince);
    }
  }

  console.log('📋 Final extracted values:', {
    street,
    city, 
    state,
    province,
    zipCode
  });

  setNewAddress((prev) => ({
    ...prev,
    street: street || prev.street,
    city: city || prev.city,
    state: state || prev.state,     // Suburb
    province: province || prev.province, // Province
    zipCode: zipCode || prev.zipCode,
  }));
  
  setAddressErrors({ street: '', city: '', state: '', province: '', zipCode: '', server: '' });
  setShowSuggestions(false);
};

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewAddress((prev) => ({ ...prev, [name]: value }));
    setAddressErrors((prev) => ({ ...prev, [name]: '', server: '' }));
    if (name === 'street') {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => fetchAddressSuggestions(value), 300);
    }
  };

  // Validate form
const validateForm = () => {
  let isValid = true;
  const newErrors = { street: '', city: '', state: '', province: '', zipCode: '', server: '' };
  
  if (!newAddress.street.trim()) {
    newErrors.street = 'Street is required';
    isValid = false;
  }
  if (!newAddress.city.trim()) {
    newErrors.city = 'City/Town is required';
    isValid = false;
  }
  if (!newAddress.state.trim()) {
    newErrors.state = 'Suburb is required';
    isValid = false;
  }
  if (!newAddress.zipCode.trim()) {
    newErrors.zipCode = 'Postal Code is required';
    isValid = false;
  } else {
    // Temporary relaxed validation - just check if it contains at least 3 digits
    const digitCount = newAddress.zipCode.replace(/\D/g, '').length;
    if (digitCount < 3) {
      newErrors.zipCode = 'Postal Code must contain at least 3 digits';
      isValid = false;
    }
  }
  
  setAddressErrors(newErrors);
  return isValid;
};

  // Add new address
const handleAddAddress = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!clientId || isNaN(parseInt(clientId))) {
    setAddressErrors((prev) => ({ ...prev, server: 'Client ID is missing or invalid' }));
    return;
  }

  if (validateForm()) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // ENHANCED ZIP CODE SANITIZATION
      console.log('🔍 DEEP ZIP CODE DEBUGGING:');
      console.log('  Original zipCode:', `"${newAddress.zipCode}"`);
      console.log('  Type:', typeof newAddress.zipCode);
      console.log('  Length:', newAddress.zipCode.length);
      console.log('  Character codes:', newAddress.zipCode.split('').map((char, index) => 
        `pos ${index}: '${char}' = code ${char.charCodeAt(0)}`
      ));

      // Aggressive cleaning
      const step1 = newAddress.zipCode.toString();
      const step2 = step1.replace(/[^\d]/g, '');
      const step3 = step2.replace(/\s/g, '');
      const sanitizedZip = step3.substring(0, 4);
      const finalZip = sanitizedZip.padStart(4, '0');
      
      console.log('🔧 ZIP Processing Steps:', {
        step1: `"${step1}"`,
        step2: `"${step2}"`,
        step3: `"${step3}"`,
        sanitized: `"${sanitizedZip}"`,
        final: `"${finalZip}"`,
        finalLength: finalZip.length
      });

      // Validate after cleaning
      if (finalZip.length !== 4) {
        throw new Error(`ZIP code must be exactly 4 digits after cleaning. Got: "${newAddress.zipCode}" -> "${finalZip}" (${finalZip.length} digits)`);
      }

      const payload = { 
        street: newAddress.street,
        city: newAddress.city,
        state: newAddress.state,
        province: newAddress.province,
        zipCode: finalZip, 
        clientId: parseInt(clientId) 
      };

      console.log('🎯 Final payload with suburb:', payload);
      console.log('🏘️ Suburb being sent:', newAddress.state);

      const response = await axios.post(
        `${API_URL}/api/addresses`,
        payload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

            setAddresses(prevAddresses => [...prevAddresses, response.data]);
      setNewAddress({ street: '', city: '', state: '', province: '', zipCode: '' });
      setAddressErrors((prev) => ({ ...prev, server: '' }));
      setIsAddressModalOpen(false);
      
      // ✅ Refresh audit logs
      if (onAddressChange) {
        onAddressChange();
      }

    } catch (error: any) {
      console.error('❌ Error adding address:', error);

      if (error.response) {
        console.error('📩 Backend responded with:', error.response.data);
        console.error('📦 Backend response status:', error.response.status);
        
        // More detailed error handling
        if (error.response.status === 400) {
          const backendMessage = error.response.data.message;
          setAddressErrors((prev) => ({
            ...prev,
            server: `Backend validation error: ${backendMessage}. Please check the ZIP code format.`,
          }));
        }
      } else {
        // This handles our custom validation errors
        setAddressErrors((prev) => ({
          ...prev,
          server: error.message || 'Network error: Could not connect to server',
        }));
      }
    }
  }
};

  // Edit existing address
const handleEditAddress = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!clientId || isNaN(parseInt(clientId)) || !editingAddressId) {
    setAddressErrors((prev) => ({ ...prev, server: 'Client ID or Address ID is missing or invalid' }));
    return;
  }

  if (validateForm()) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // Use the same enhanced ZIP code sanitization
      const step1 = newAddress.zipCode.toString();
      const step2 = step1.replace(/[^\d]/g, '');
      const step3 = step2.replace(/\s/g, '');
      const sanitizedZip = step3.substring(0, 4);
      const finalZip = sanitizedZip.padStart(4, '0');
      
      console.log('🔧 Edit - ZIP Processing:', {
        original: `"${newAddress.zipCode}"`,
        final: `"${finalZip}"`,
        length: finalZip.length
      });

      if (finalZip.length !== 4) {
        throw new Error(`ZIP code must be exactly 4 digits after cleaning. Got: "${newAddress.zipCode}" -> "${finalZip}"`);
      }

      const payload = { 
        street: newAddress.street,
        city: newAddress.city,
        state: newAddress.state,     // This is the suburb - ensure it's included
        province: newAddress.province,
        zipCode: finalZip, 
        clientId: parseInt(clientId) 
      };

      console.log('🎯 Final edit payload with suburb:', payload);
      console.log('🏘️ Suburb being sent for edit:', newAddress.state);

      const response = await axios.put(
        `${API_URL}/api/addresses/${editingAddressId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Preserve the original order by updating the specific address at its original position
            setAddresses(prevAddresses => 
        prevAddresses.map(addr => 
          addr.id === editingAddressId ? response.data : addr
        )
      );

      setNewAddress({ street: '', city: '', state: '', province: '', zipCode: '' });
      setAddressErrors((prev) => ({ ...prev, server: '' }));
      setIsAddressModalOpen(false);
      setEditingAddressId(null);
      
      // ✅ Refresh audit logs
      if (onAddressChange) {
        onAddressChange();
      }

    } catch (error: any) {
      console.error('❌ Error editing address:', error);

      if (error.response) {
        console.error('📩 Backend responded with:', error.response.data);
        setAddressErrors((prev) => ({
          ...prev,
          server: error.response?.data?.message || 'Failed to update address',
        }));
      } else {
        setAddressErrors((prev) => ({
          ...prev,
          server: error.message || 'Failed to update address',
        }));
      }
    }
  }
};

  // Open add modal
  const openAddModal = () => {
    setNewAddress({ street: '', city: '', state: '', province: '', zipCode: '' });
    setAddressErrors({ street: '', city: '', state: '', province: '', zipCode: '', server: '' });
    setEditingAddressId(null);
    setIsAddressModalOpen(true);
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  // Open edit modal
  const openEditModal = (address: Address) => {
    setNewAddress({
      street: address.street,
      city: address.city,
      state: address.state,
      province: address.province || '',
      zipCode: address.zipCode,
    });
    setAddressErrors({ street: '', city: '', state: '', province: '', zipCode: '', server: '' });
    setEditingAddressId(address.id!);
    setIsAddressModalOpen(true);
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  // Close modal
  const closeModal = () => {
    setIsAddressModalOpen(false);
    setNewAddress({ street: '', city: '', state: '', province: '', zipCode: '' });
    setAddressErrors({ street: '', city: '', state: '', province: '', zipCode: '', server: '' });
    setEditingAddressId(null);
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Address</h3>
        {can('address', 'write') && (
          <button
            onClick={openAddModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Add New Address
          </button>
        )}
      </div>

      {addressErrors.server && (
        <p className="text-red-500 text-sm mb-4">
          {addressErrors.server.includes('Server error') ? `Server error: Please check the backend logs for details.` : addressErrors.server}
        </p>
      )}
      {addresses.length > 0 ? (
        <div className="space-y-6 mb-6">
          {addresses.map((address: Address, index: number) => (
            <div key={address.id || `address-${index}`} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <h4 className="text-md font-semibold">Address {index + 1}</h4>
                    {can('address', 'write') && (
                      <button
                        onClick={() => openEditModal(address)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 text-sm"
                      >
                        Edit
                      </button>
                    )}
                  </div>

              <p className="text-sm font-medium text-gray-700">Street: {address.street}</p>
              <p className="text-sm font-medium text-gray-700">Suburb: {address.state}</p>
              <p className="text-sm font-medium text-gray-700">City/Town: {address.city}</p>
              <p className="text-sm font-medium text-gray-700">
                <span className="font-semibold">Province:</span> {address.province || 'N/A'}
              </p>
              <p className="text-sm font-medium text-gray-700">Postal Code: {address.zipCode || 'N/A'}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 mb-6">No addresses added yet.</p>
      )}
      {isAddressModalOpen && (
        <AddressModal
          clientId={clientId}
          onClose={closeModal}
          onSubmit={handleAddAddress}
          onEdit={handleEditAddress}
          newAddress={newAddress}
          addressErrors={addressErrors}
          editingAddressId={editingAddressId}
          addressSuggestions={addressSuggestions}
          showSuggestions={showSuggestions}
          autocompleteInputRef={autocompleteInputRef}
          handleInputChange={handleInputChange}
          handleAddressSelect={handleAddressSelect}
        />
      )}
    </div>
  );
};

export default AddressTab;